import clsx from 'clsx';
import { ReactNode } from 'react';
import { TokenIcons } from '../../assets/icons';
import { Tokens } from '../../config/constants';
import Button from '../Button';
import TextInput, { TextInputProps } from '../TextInput';

type BaseProps<TLabel, TSideLabel> = Omit<TextInputProps, 'variant' | 'className'> & {
    variant?: 'primary' | 'secondary';
    label: TLabel;
    sideLabel?: TSideLabel;
    disabled?: boolean;
    className?: string;
    renderLabel?: (label: TLabel) => ReactNode;
    onMax?: () => void;
    onSideLabelClick?: () => void;
};

type AssetProps<TLabel, TSideLabel> = BaseProps<TLabel, TSideLabel> & {
    asset: Tokens;
    withIcon?: boolean;
};

type WithIcon<TLabel, TSideLabel> = BaseProps<TLabel, TSideLabel> & {
    asset: string;
    withIcon?: never;
};

type Props<TLabel, TSideLabel> =
    | AssetProps<TLabel, TSideLabel>
    | WithIcon<TLabel, TSideLabel>;

const AssetInput = <TLabel extends ReactNode, TSideLabel extends ReactNode>({
    variant = 'primary',
    label,
    sideLabel,
    asset,
    disabled = false,
    className,
    withIcon = Object.values<string>(Tokens).includes(asset) ? true : undefined,
    onMax,
    renderLabel,
    onSideLabelClick,
    ...textInputProps
}: Props<TLabel, TSideLabel>) => {
    return (
        <div className={clsx(['asset-input-container', variant, className])}>
            <div className='label-container'>
                {renderLabel ? (
                    renderLabel(label)
                ) : (
                    <span className='label'>{label}</span>
                )}

                {sideLabel && (
                    <span
                        className={clsx('side-label', {
                            clickable: !!onSideLabelClick && !disabled,
                        })}
                        onClick={disabled ? undefined : onSideLabelClick}
                    >
                        {sideLabel}
                    </span>
                )}
            </div>
            <div className='input'>
                <TextInput
                    disabled={disabled}
                    variant='number'
                    placeholder='0.00'
                    {...textInputProps}
                />
                <div className='asset'>
                    {withIcon && (TokenIcons[asset as Tokens] ?? TokenIcons.FALLBACK)}
                    <div className='asset-name'>
                        <span>{asset}</span>
                        {!!onMax && (
                            <Button
                                className='max-available'
                                onClick={onMax}
                                disabled={disabled}
                            >
                                Max
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AssetInput;
